import type { AgentNodeData } from '@saaga/shared/interfaces/agency-node.interface'
import type { AgentTool } from '@saaga/shared/interfaces/agent-tool.interface'
import type { LlmModel } from '@saaga/shared/interfaces/llm-model.interface'
import type { ReactNode } from 'react'

import { ResizableDrawer } from '@/components/ui/resizable-drawer.organism'
import { useIsMobile } from '@/hooks/use-mobile.hook'

import { AgentNodeDrawerContent } from './agent-node-drawer-content.molecule'
import { AgentNodeUnsavedChangesDialog } from './agent-node-unsaved-changes-dialog.atom'
import { useAgentNodeDrawer } from './use-agent-node-drawer.hook'

export interface AgentNodeResizableDrawerProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  nodeData: AgentNodeData | null
  nodeId: string | null
  onSave: (nodeId: string, data: Partial<AgentNodeData>) => void
  modelList: LlmModel[]
  isLoadingModels?: boolean
  toolList: AgentTool[]
  isLoadingTools?: boolean
}

export const AgentNodeResizableDrawer = ({
  isOpen,
  onOpenChange,
  nodeData,
  nodeId,
  onSave,
  modelList,
  isLoadingModels = false,
  toolList,
  isLoadingTools = false,
}: AgentNodeResizableDrawerProps): ReactNode => {
  const isMobile = useIsMobile()
  const drawer = useAgentNodeDrawer({ nodeData, nodeId, onSave, onOpenChange })

  return (
    <>
      <ResizableDrawer
        isOpen={isOpen}
        onOpenChange={(open) => (open ? onOpenChange(open) : drawer.handleCloseAttempt())}
        title="Edit Agent"
        description="Configure agent properties"
        className="agent-node-drawer"
      >
        <AgentNodeDrawerContent
          form={drawer.form}
          nodeId={nodeId}
          isEntryPoint={nodeData?.isEntryPoint}
          isMobile={isMobile}
          modelList={modelList}
          isLoadingModels={isLoadingModels}
          toolList={toolList}
          isLoadingTools={isLoadingTools}
          imagePreviewUrl={drawer.imagePreviewUrl}
          onImageChange={drawer.handleImageChange}
          onImageRemove={drawer.handleImageRemove}
          onCancel={drawer.handleCloseAttempt}
          onSubmit={drawer.handleSubmit}
        />
      </ResizableDrawer>
      <AgentNodeUnsavedChangesDialog
        isOpen={drawer.isUnsavedDialogOpen}
        onClose={drawer.handleUnsavedDialogClose}
        onConfirm={drawer.handleForceClose}
      />
    </>
  )
}

AgentNodeResizableDrawer.displayName = 'AgentNodeResizableDrawer'
